import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Bot,
  Check,
  ChevronRight,
  CornerDownRight,
  PackageX,
  X,
} from "lucide-react";
import { useAdminDashboard, useDismissAlert } from "@/lib/queries";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import {
  StatusBadge,
  orderStatusTone,
  humaniseStatus,
  type StatusTone,
} from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn, formatDateTime } from "@/lib/utils";
import type { DashboardData } from "@/lib/types";

// Admin Dashboard — summary stat cards, recent orders, recent alerts.
export function AdminDashboard() {
  const { data, isLoading } = useAdminDashboard();
  const d = data?.data;

  const stats = [
    {
      label: "Total Orders (30d)",
      value: d?.stats.totalOrders30d ?? 0,
      key: "orders",
    },
    {
      label: "Active Back-Orders",
      value: d?.stats.activeBackOrders ?? 0,
      key: "backorders",
    },
    {
      label: "Products in Catalog",
      value: d?.stats.productsInCatalog ?? 0,
      key: "products",
    },
    {
      label: "Active Locations",
      value: d?.stats.activeLocations ?? 0,
      key: "locations",
    },
  ];

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "alerts" ? "alerts" : "orders";
  const alerts = d?.recentAlerts ?? [];
  const orders = d?.recentOrders ?? [];

  return (
    <AdminLayout>
      <div className="mb-10">
        <h1 className="page-title">Dashboard</h1>
        <p className="page-sub mt-1">Overview across all locations.</p>
      </div>

      <div className="mb-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((s) => (
          <Card key={s.key} className="border-t-2 border-t-brand">
            <CardContent className="p-6">
              <div className="font-head text-4xl font-extrabold text-ink">
                {isLoading ? "—" : s.value}
              </div>
              <div className="mt-2 text-sm text-muted">{s.label}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* One panel, tabbed; it fills the rest of the viewport and scrolls inside. */}
      <div className="card flex max-h-[75vh] min-h-0 flex-col lg:h-[calc(100vh-24rem)] lg:min-h-[30rem] lg:max-h-none">
        <div className="flex shrink-0 items-end gap-8 border-b border-line px-8" role="tablist">
          <TabButton active={tab === "orders"} onClick={() => setSearchParams({})}>
            Current orders
            <span className="ml-2 font-mono text-sm text-faint">{orders.length}</span>
          </TabButton>
          <TabButton active={tab === "alerts"} onClick={() => setSearchParams({ tab: "alerts" })}>
            Alerts
            {alerts.length > 0 && (
              <span className="ml-2 rounded-full bg-danger/15 px-2 py-0.5 font-mono text-xs text-danger">
                {alerts.length}
              </span>
            )}
          </TabButton>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
          {tab === "orders" ? <OrdersTable orders={orders} /> : <AlertsBySeverity alerts={alerts} />}
        </div>
      </div>
    </AdminLayout>
  );
}

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "-mb-px flex items-center border-b-2 pb-4 pt-6 font-head text-lg font-extrabold uppercase tracking-wide transition-colors",
        active ? "border-brand text-ink" : "border-transparent text-muted hover:text-ink",
      )}
    >
      {children}
    </button>
  );
}

function OrdersTable({ orders }: { orders: DashboardData["recentOrders"] }) {
  if (orders.length === 0) return <p className="py-10 text-center text-muted">No current orders.</p>;
  return (
    <table className="w-full text-left text-sm">
      <thead className="sticky -top-6 z-10 bg-surface">
        <tr className="border-b border-line text-muted">
          <th className="pb-3 pr-6 pt-0 font-medium">Order #</th>
          <th className="pb-3 pr-6 font-medium">Submitted</th>
          <th className="pb-3 pr-6 font-medium">Location</th>
          <th className="pb-3 pr-6 font-medium">Chef</th>
          <th className="pb-3 font-medium">Overall status</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id} className="border-b border-line transition-colors hover:bg-surface-2/60">
            <td className="py-4 pr-6 font-mono">
              <Link to={`/admin/orders/${o.id}`} className="text-brand hover:underline">
                {o.orderNumber}
              </Link>
            </td>
            <td className="py-4 pr-6 text-muted">{formatDateTime(o.submittedAt)}</td>
            <td className="py-4 pr-6 text-ink">{o.locationName}</td>
            <td className="py-4 pr-6 text-ink">{o.chefName}</td>
            <td className="py-4">
              <StatusBadge tone={orderStatusTone(o.status)}>{humaniseStatus(o.status)}</StatusBadge>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type Severity = "critical" | "approval" | "info";

const ALERT_KIND: Record<
  string,
  { label: string; severity: Severity; icon: typeof Bot }
> = {
  unfulfillable: {
    label: "Can't fulfil",
    severity: "critical",
    icon: PackageX,
  },
  manual_intervention_required: {
    label: "Action needed",
    severity: "critical",
    icon: AlertTriangle,
  },
  agent_approval_required: {
    label: "Needs approval",
    severity: "approval",
    icon: Bot,
  },
  back_order_alert: {
    label: "Rerouted",
    severity: "info",
    icon: CornerDownRight,
  },
};

const SEVERITY: Record<
  Severity,
  { rank: number; tone: StatusTone; row: string; icon: string }
> = {
  critical: {
    rank: 0,
    tone: "red",
    row: "border-l-danger bg-danger/[0.07]",
    icon: "bg-danger/15 text-danger",
  },
  approval: {
    rank: 1,
    tone: "cyan",
    row: "border-l-brand bg-brand/[0.05]",
    icon: "bg-brand/15 text-brand",
  },
  info: {
    rank: 2,
    tone: "amber",
    row: "border-l-amber/60",
    icon: "bg-amber/15 text-amber",
  },
};

type Alert = DashboardData["recentAlerts"][number];
const kindOf = (a: Alert) => ALERT_KIND[a.type] ?? ALERT_KIND.back_order_alert;

const GROUPS: Array<{ severity: Severity; title: string; hint: string; defaultOpen: boolean }> = [
  { severity: "critical", title: "Critical", hint: "Orders that can't go through without you", defaultOpen: true },
  { severity: "approval", title: "Needs approval", hint: "The AI agent is waiting for your OK", defaultOpen: true },
  { severity: "info", title: "Rerouted", hint: "Handled automatically — for your information", defaultOpen: false },
];

// Alerts grouped by severity, each group collapsible; newest first inside.
function AlertsBySeverity({ alerts }: { alerts: Alert[] }) {
  const [open, setOpen] = useState<Record<Severity, boolean>>(
    () => Object.fromEntries(GROUPS.map((g) => [g.severity, g.defaultOpen])) as Record<Severity, boolean>,
  );
  if (alerts.length === 0) return <p className="py-10 text-center text-muted">All caught up — no alerts.</p>;

  return (
    <div className="space-y-8">
      {GROUPS.map((g) => {
        const items = alerts
          .filter((a) => kindOf(a).severity === g.severity)
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        if (items.length === 0) return null;
        const isOpen = open[g.severity];
        const sev = SEVERITY[g.severity];
        return (
          <section key={g.severity}>
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [g.severity]: !o[g.severity] }))}
              aria-expanded={isOpen}
              className="group flex w-full items-center gap-3 text-left"
            >
              <ChevronRight
                className={cn("h-4 w-4 text-muted transition-transform", isOpen && "rotate-90")}
              />
              <span className="font-head text-base font-extrabold uppercase tracking-wide text-ink">{g.title}</span>
              <StatusBadge tone={sev.tone}>{items.length}</StatusBadge>
              <span className="hidden text-sm text-faint sm:inline">{g.hint}</span>
              <span className="ml-auto h-px flex-1 bg-line" aria-hidden />
            </button>
            {isOpen && (
              <ul className="mt-4 space-y-3">
                {items.map((a) => (
                  <AlertItem key={a.id} alert={a} />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

// One alert row. AI-agent approvals get Approve / Review (demo: Approve shows
// "Approved" then dismisses). Every alert can be dismissed (marked read).
function AlertItem({ alert: a }: { alert: Alert }) {
  const [approved, setApproved] = useState(false);
  // Open the order on the supplier this alert is about, scrolled to its trace.
  const orderLink = a.orderId
    ? `/admin/orders/${a.orderId}${a.supplierId ? `?supplier=${a.supplierId}` : ""}`
    : null;
  const dismiss = useDismissAlert();
  const kind = kindOf(a);
  const sev = SEVERITY[kind.severity];
  const Icon = kind.icon;

  function approve() {
    setApproved(true);
    setTimeout(() => dismiss.mutate(a.id), 1200);
  }

  return (
    <li
      className={cn(
        "group flex items-start gap-4 border-l-[3px] px-5 py-4 transition-opacity",
        approved ? "border-l-success bg-success/[0.06]" : sev.row,
        dismiss.isPending && "opacity-40",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
          approved ? "bg-success/15 text-success" : sev.icon,
        )}
      >
        {approved ? (
          <Check className="h-4 w-4" />
        ) : (
          <Icon className="h-4 w-4" />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <StatusBadge tone={approved ? "green" : sev.tone}>
            {approved ? "Approved" : kind.label}
          </StatusBadge>
          {orderLink && (
            <Link
              to={orderLink}
              className="font-mono text-xs text-brand hover:underline"
            >
              {a.orderNumber ?? "View order"}
            </Link>
          )}
          <span className="text-xs text-faint">
            {formatDateTime(a.createdAt)}
          </span>
        </div>
        <div className="text-sm leading-relaxed text-ink">{a.message}</div>
        {kind.severity === "approval" && !approved && (
          <div className="mt-3 flex gap-2">
            <Button size="sm" onClick={approve}>
              <Check className="h-3.5 w-3.5" /> Approve
            </Button>
            {orderLink && (
              <Button size="sm" variant="outline" asChild>
                <Link to={orderLink}>Review</Link>
              </Button>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => dismiss.mutate(a.id)}
        disabled={dismiss.isPending}
        title="Dismiss"
        aria-label="Dismiss alert"
        className="shrink-0 rounded p-1 text-faint transition-colors hover:bg-surface-3 hover:text-ink"
      >
        <X className="h-4 w-4" />
      </button>
    </li>
  );
}
